import React, { useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSortableTable } from "../../../../../hooks/sorting/useSortableTable";
import SortableHeader from "../../../../../components/common/sorting/SortableHeader";
import { useColumnResize, ResizeHandle } from "../../../../../components/common/ResizeColumns";

// ── Helpers ───────────────────────────────────────────────────────────────────

const AVATAR_COLORS = ["#28B3B4", "#D6DF27", "#4a9960", "#7a5abd", "#c88a3a"];
const avatarColor = (name = "") =>
  AVATAR_COLORS[((name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0)) % AVATAR_COLORS.length];

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "2-digit",
  });
};

const allocationDisplay = (row) => {
  switch (row.allocationStatus) {
    case "FULLY_ALLOCATED":
    case "PARTIAL":
      return { position: row.currentPosition || "—", mac: row.currentMacName || "—" };
    case "IN_SCG_POOL":
      return { position: "In SCG Pool", mac: null, italic: true };
    case "NOT_ALLOCATED":
    default:
      return { position: "Not allocated", mac: null, italic: true };
  }
};

// ── Default column widths ─────────────────────────────────────────────────────
const DEFAULT_COL_WIDTHS     = [220, 120, 180, 180, 160, 240, 120, 120];
const DEFAULT_COL_WIDTHS_SCG = [200, 110, 170, 170, 150, 220, 110, 110, 120];

// ── Column definitions (shared between header map and index lookup) ───────────
const getColDefs = (isSCG) => [
  { label: "Name",                   sortKey: "fullName"  },
  { label: "Level",                  sortKey: "levelCode" },
  { label: "Skills (from Zoho)",     sortKey: null        },
  { label: "Allocation",             sortKey: "usedFte"   },
  { label: "Reporting Manager",      sortKey: null        },  // ← ADD
  { label: "Current MAC / Position", sortKey: null        },
  { label: "Joined",                 sortKey: "joinDate"  },
  { label: "",                       sortKey: null        },
  ...(isSCG ? [{ label: "SCG Bench", sortKey: null }] : []),
];

// ── Custom comparators ────────────────────────────────────────────────────────
const ASSOCIATE_COMPARATORS = {
  usedFte: (a, b) => (a.usedFte ?? -1) - (b.usedFte ?? -1),
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SkeletonRow = ({ t, cols }) => (
  <Box sx={{
    display: "grid", gridTemplateColumns: cols,
    padding: "12px 20px", gap: "8px",
    borderBottom: `1px solid ${t.border}`,
    alignItems: "center",
  }}>
    {[140, 70, 120, 100, 100, 70, 80].map((w, i) => (
      <Box key={i} sx={{
        height: "11px", borderRadius: "3px",
        width: `${w}px`, maxWidth: "100%",
        background: t.surface3, opacity: 0.6,
      }} />
    ))}
  </Box>
);

const EmptyState = ({ t }) => (
  <Box sx={{ display: "flex", justifyContent: "center", padding: "60px 24px" }}>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "12px", color: t.text3, letterSpacing: "0.08em",
    }}>
      No associates found
    </Typography>
  </Box>
);

// ── Main ──────────────────────────────────────────────────────────────────────

const AssociateTable = ({ rows, loading, onView, onRequest, isSCG = false, onAddToBench, onViewHistory }) => {
  const theme = useTheme();
  const t     = theme.custom;

  const [benchState, setBenchState] = useState({});
  const [expandedSkills, setExpandedSkills] = useState({});
const scrollRef = React.useRef(null);

  // ── Column resize ──────────────────────────────────────────────────────────
  const { buildGrid, getHandleProps } = useColumnResize(
    isSCG ? DEFAULT_COL_WIDTHS_SCG : DEFAULT_COL_WIDTHS
  );

  // ── Sorting ────────────────────────────────────────────────────────────────
  const { sortedRows, sortKey, sortDir, handleSort } = useSortableTable(rows, {
    defaultKey: "fullName",
    defaultDir: "asc",
    comparators: ASSOCIATE_COMPARATORS,
  });

  // ── Column definitions ─────────────────────────────────────────────────────
  const colDefs = getColDefs(isSCG);

  // ── Shared header cell style ───────────────────────────────────────────────
  const thSx = {
    fontFamily: "'DM Mono', monospace",
    fontSize: "9px",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: t.text3,
    userSelect: "none",
    whiteSpace: "nowrap",
    background: t.surface,
    border: "none",
    padding: 0,
    textAlign: "left",
    position: "sticky",
    top: 0,
    zIndex: 1,
    fontWeight: 500,
  };

  const handleBench = async (row) => {
    const id = row.associateId;
    setBenchState((prev) => ({ ...prev, [id]: "loading" }));
    try {
      await onAddToBench(id);
      setBenchState((prev) => ({ ...prev, [id]: "done" }));
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed";
      setBenchState((prev) => ({ ...prev, [id]: msg }));
      setTimeout(() => {
        setBenchState((prev) => ({ ...prev, [id]: "idle" }));
      }, 3000);
    }
  };

  return (
    <Box ref={scrollRef} sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", minWidth: 0 }}>

      {/* ── Sticky header ── */}
      <Box
        component="div"
        sx={{
          display: "grid",
          gridTemplateColumns: buildGrid(),
          padding: "10px 20px",
          gap: "8px",
          borderBottom: `1px solid ${t.border2}`,
          position: "sticky",
          top: 0,
          background: t.surface,
          zIndex: 1,
          minWidth: 0,
        }}
      >
        {colDefs.map(({ label, sortKey: sk }, colIndex) =>
          sk ? (
            /* ── Sortable header cell ── */
            <Box
              key={sk}
              component="div"
              onClick={() => handleSort(sk)}
              sx={{
                ...thSx,
                position: "relative",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Typography sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "9px",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: t.text3,
                fontWeight: 500,
                userSelect: "none",
                whiteSpace: "nowrap",
              }}>
                {label}
              </Typography>
              <span style={{
                opacity: sortKey === sk ? 0.85 : 0.4,
                fontSize: "10px",
                color: t.text3,
              }}>
                {sortKey === sk ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}
              </span>
              <ResizeHandle handleProps={getHandleProps(colIndex)} />
            </Box>
          ) : (
            /* ── Non-sortable header cell ── */
            <Box
              key={label || `_empty_${colIndex}`}
              sx={{
                ...thSx,
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "9px",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: t.text3,
                fontWeight: 500,
                userSelect: "none",
                whiteSpace: "nowrap",
              }}>
                {label}
              </Typography>
              <ResizeHandle handleProps={getHandleProps(colIndex)} />
            </Box>
          )
        )}
      </Box>

      {/* ── Skeleton rows ── */}
      {loading && [1, 2, 3, 4, 5].map((i) => (
        <SkeletonRow key={i} t={t} cols={buildGrid()} />
      ))}

      {/* ── Empty state ── */}
      {!loading && rows.length === 0 && <EmptyState t={t} />}

      {/* ── Data rows ── */}
      {!loading && sortedRows.map((row, i) => {
        const color   = avatarColor(row.fullName);
        const alloc   = allocationDisplay(row);
        const usedPct = Math.round((row.usedFte ?? 0) * 100);
        const barColor = usedPct >= 100 ? t.accent : t.gold;

        const bState    = benchState[row.associateId] || "idle";
        const isDone    = bState === "done";
        const isLoading = bState === "loading";
        const isErr     = bState !== "idle" && bState !== "loading" && bState !== "done";
        const showBench = isSCG && row.isNew && row.allocationStatus !== "IN_SCG_POOL" && row.isbillable !== false;

        return (
          <Box
            key={row.associateId || i}
            onClick={() => onViewHistory?.(row)}
            sx={{
              display: "grid",
              gridTemplateColumns: buildGrid(),
              padding: "10px 20px",
              gap: "8px",
              borderBottom: i < sortedRows.length - 1 ? `1px solid ${t.border}` : "none",
              alignItems: "start",
              minWidth: 0,
              transition: "background 0.15s",
              cursor: onViewHistory ? "pointer" : "default",
              "&:hover": { background: t.surface2 },
            }}
          >
            {/* ── Name ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0, pt: "2px" }}>
              <Box sx={{
                width: "30px", height: "30px", borderRadius: "50%", flexShrink: 0,
                background: `${color}22`, border: `1px solid ${color}55`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'DM Mono', monospace", fontSize: "9px", fontWeight: 700, color,
              }}>
                {row.initials || row.fullName?.slice(0, 2).toUpperCase()}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "12.5px", fontWeight: 600, color: t.text,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {row.fullName}
                </Typography>
                {row.isNew && row.isbillable !== false && (
  <Box sx={{
    display: "inline-flex", alignItems: "center", gap: "3px",
    mt: "2px", background: t.accent2,
    border: `1px solid ${t.accent}44`,
    borderRadius: "2px", padding: "0px 5px",
  }}>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace", fontSize: "7.5px",
      letterSpacing: "0.06em", color: t.accent, fontWeight: 700,
    }}>
      ⚑ New
    </Typography>
  </Box>
)}
{row.isbillable === false && (
  <Box sx={{
    display: "inline-flex", alignItems: "center", gap: "3px",
    mt: "2px",
    background: t.mode === "dark" ? "#2a2000" : "#fdf6d8",
    border: t.mode === "dark"
      ? "1px solid #D6DF2755"
      : "1px solid #c9b800aa",
    borderRadius: "2px", padding: "0px 5px",
  }}>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace", fontSize: "7.5px",
      letterSpacing: "0.06em",
      color: t.mode === "dark" ? "#D6DF27" : "#7a6800",
      fontWeight: 700,
    }}>
      ⊘ Non-Billable
    </Typography>
  </Box>
)}
{row.associateType === "SUBCONTRACTOR" && (
  <Box sx={{
    display: "inline-flex", alignItems: "center",
    mt: "2px", padding: "0px 5px", borderRadius: "2px",
    background: "rgba(214,223,39,0.12)",
    border: "1px solid rgba(214,223,39,0.35)",
  }}>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace", fontSize: "7.5px",
      letterSpacing: "0.06em", fontWeight: 700, color: t.gold,
    }}>
      CONTRACTOR
    </Typography>
  </Box>
)}
{row.department?.toUpperCase() === "TAC" && (
  <Box sx={{
    display: "inline-flex", alignItems: "center",
    mt: "2px", padding: "0px 5px", borderRadius: "2px",
    background: "rgba(100,140,200,0.12)",
    border: "1px solid rgba(100,140,200,0.30)",
  }}>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace", fontSize: "7.5px",
      letterSpacing: "0.06em", fontWeight: 700, color: "#7ba4d4",
    }}>
      TAC
    </Typography>
  </Box>
)}
{(row.department?.toUpperCase() === "BT" || row.department?.toUpperCase() === "TSG") && (
  <Box sx={{
    display: "inline-flex", alignItems: "center",
    mt: "2px", padding: "0px 5px", borderRadius: "2px",
    background: "rgba(150,120,200,0.12)",
    border: "1px solid rgba(150,120,200,0.30)",
  }}>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace", fontSize: "7.5px",
      letterSpacing: "0.06em", fontWeight: 700, color: "#a98fcc",
    }}>
      BT
    </Typography>
  </Box>
)}
              </Box>
            </Box>

            {/* ── Level ── */}
            <Box>
              <Box sx={{
                background: t.surface3, borderRadius: "2px",
                padding: "2px 6px", display: "inline-flex", mb: "2px",
              }}>
                <Typography sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "10px", color: t.goldText, fontWeight: 700,
                }}>
                  {row.levelCode}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "9px", color: t.text3, lineHeight: 1.3 }}>
                {row.levelName}
              </Typography>
            </Box>

           {/* ── Skills ── */}
<Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", minWidth: 0 }}>
  {row.skills?.length > 0 ? (
    <>
      {(expandedSkills[row.associateId] ? row.skills : row.skills.slice(0, 3)).map((sk) => (
        <Box key={sk} sx={{
          background: t.surface2, border: `1px solid ${t.border2}`,
          borderRadius: "2px", padding: "1px 7px",
        }}>
          <Typography sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "10px", color: t.text2,
            whiteSpace: "nowrap",
          }}>
            {sk}
          </Typography>
        </Box>
      ))}
      {row.skills.length > 3 && (
        <Box
          onClick={() => setExpandedSkills((prev) => ({ ...prev, [row.associateId]: !prev[row.associateId] }))}
          sx={{
            background: t.surface3, borderRadius: "2px", padding: "1px 6px",
            cursor: "pointer",
            "&:hover": { background: t.surface2 },
          }}
        >
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3 }}>
            {expandedSkills[row.associateId] ? "− less" : `+${row.skills.length - 3}`}
          </Typography>
        </Box>
      )}
    </>
  ) : (
    <Typography sx={{ fontSize: "10px", color: t.text3, fontStyle: "italic" }}>
      No skills synced
    </Typography>
  )}
</Box>

            {/* ── Allocation bar ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "7px", minWidth: 0, pt: "2px" }}>
              <Box sx={{
                width: "60px", height: "5px", flexShrink: 0,
                background: t.surface3, borderRadius: "3px", overflow: "hidden",
              }}>
                <Box sx={{
                  height: "100%", width: `${usedPct}%`,
                  background: barColor, borderRadius: "3px",
                }} />
              </Box>
              <Typography sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "11px", fontWeight: 600,
                color: usedPct >= 100 ? t.accent : t.gold,
                whiteSpace: "nowrap",
              }}>
                {row.usedFte} / 1.0
              </Typography>
            </Box>

            {/* ── Reporting Manager ── */}
<Box sx={{ minWidth: 0 }}>
  <Typography sx={{
    fontFamily: "'Inter', sans-serif",
    fontSize: "11px",
    color: t.text2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }}>
    {row.reportingManager || "—"}
  </Typography>
</Box>

           {/* ── Current MAC / Position ── */}
            <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
              {row.activeAllocations?.length > 0 ? (
                row.activeAllocations.map((alloc, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: "6px",
                      padding: "3px 7px",
                      background: t.surface2,
                      border: `1px solid ${t.border2}`,
                      borderRadius: "3px",
                      minWidth: 0,
                    }}
                  >
                    {/* Position Code */}
                    <Typography sx={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "10px", fontWeight: 600,
                      color: t.accent,
                      whiteSpace: "nowrap", flexShrink: 0,
                    }}>
                      {alloc.positionCode}
                    </Typography>

                    {/* Divider dot */}
                    <Box sx={{
                      width: "3px", height: "3px", borderRadius: "50%",
                      background: t.border2, flexShrink: 0,
                    }} />

                    {/* MAC Name */}
                    <Typography sx={{
                      fontSize: "10px", color: t.text2,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {alloc.macName}
                    </Typography>

                    {/* FTE — future field, renders only when present */}
                    {alloc.fte != null && (
                      <>
                        <Box sx={{
                          width: "3px", height: "3px", borderRadius: "50%",
                          background: t.border2, flexShrink: 0,
                        }} />
                        <Typography sx={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: "10px", fontWeight: 600,
                          color: t.gold, flexShrink: 0,
                        }}>
                          {alloc.fte}
                        </Typography>
                      </>
                    )}
                  </Box>
                ))
              ) : (
                <Typography sx={{
                  fontSize: "11px", color: t.text3, fontStyle: "italic",
                }}>
                  {alloc.italic ? alloc.position : "—"}
                </Typography>
              )}
            </Box>

            {/* ── Joined ── */}
            <Typography sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "10px", color: t.text3, whiteSpace: "nowrap",
            }}>
              {fmtDate(row.joinDate)}
            </Typography>

            {/* ── Actions placeholder ── */}
            <Box />

            {/* ── SCG Bench column ── */}
            {isSCG && (
              <Box>
                {showBench ? (
                  isDone ? (
                    <Box sx={{
                      display: "inline-flex", alignItems: "center", gap: "4px",
                      padding: "3px 8px", borderRadius: "2px",
                      background: t.accent2,
                      border: `1px solid ${t.accent}44`,
                    }}>
                      <Typography sx={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "9px", color: t.accent,
                        letterSpacing: "0.06em", fontWeight: 600,
                      }}>
                        ✓ Benched
                      </Typography>
                    </Box>
                  ) : isErr ? (
                    <Typography sx={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "9px", color: "#c84040",
                      letterSpacing: "0.04em",
                      lineHeight: 1.4,
                      maxWidth: "90px",
                    }}>
                      {bState}
                    </Typography>
                  ) : (
                    <Box
                      onClick={isLoading ? undefined : (e) => { e.stopPropagation(); handleBench(row); }}
                      sx={{
                        display: "inline-flex", alignItems: "center", gap: "5px",
                        padding: "3px 9px", borderRadius: "2px",
                        cursor: isLoading ? "not-allowed" : "pointer",
                        background: t.steel2,
                        border: `1px solid rgba(3,81,77,0.25)`,
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "10px", fontWeight: 500,
                        color: t.steel,
                        whiteSpace: "nowrap",
                        transition: "all 0.15s",
                        "&:hover": isLoading ? {} : {
                          background: t.accent2,
                          borderColor: `${t.accent}55`,
                          color: t.accent,
                        },
                      }}
                    >
                      {isLoading
                        ? <><CircularProgress size={8} sx={{ color: "inherit" }} /> Adding…</>
                        : "＋ Bench"}
                    </Box>
                  )
                ) : (
                  <Typography sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "10px", color: t.text3,
                  }}>
                    —
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default AssociateTable;